import React, {
    createContext,
    useContext,
    useMemo,
    type PropsWithChildren,
} from "react"

import type { OutfitterAssistantAgent } from "~components/outfitter-assistant/outfitter-assistant-constants"
import { AiAgent } from "~graphql/generated/types"

interface Props extends PropsWithChildren {
    readonly aiAgent: AiAgent | OutfitterAssistantAgent
    readonly selectedAiSuggestionId: string | null
    readonly setSelectedAiSuggestionId: (aiSuggestionId: string | null) => void
    readonly lastSelectedAiSuggestionId: string | null
    readonly setLastSelectedAiSuggestionId: (
        aiSuggestionId: string | null
    ) => void
    readonly acceptSuggestion: (suggestionId: string) => void
    readonly cancelSuggestion: () => void
    readonly isLoadingAcceptSuggestion: boolean
    readonly sendUserMessage?: (text: string) => void
    readonly isChatLoading?: boolean
}

interface ChatContextType {
    aiAgent: AiAgent | OutfitterAssistantAgent
    selectedAiSuggestionId: string | null
    setSelectedAiSuggestionId: (aiSuggestionId: string | null) => void
    lastSelectedAiSuggestionId: string | null
    setLastSelectedAiSuggestionId: (aiSuggestionId: string | null) => void
    acceptSuggestion: (suggestionId: string) => void
    cancelSuggestion: () => void
    isLoadingAcceptSuggestion: boolean
    sendUserMessage?: (text: string) => void
    isChatLoading: boolean
}

const ChatContext = createContext<ChatContextType>({
    aiAgent: AiAgent.Availability,
    selectedAiSuggestionId: null,
    setSelectedAiSuggestionId: () => {},
    lastSelectedAiSuggestionId: null,
    setLastSelectedAiSuggestionId: () => {},
    acceptSuggestion: () => {},
    cancelSuggestion: () => {},
    isLoadingAcceptSuggestion: false,
    isChatLoading: false,
})

export function ChatProvider({
    aiAgent,
    selectedAiSuggestionId,
    setSelectedAiSuggestionId,
    lastSelectedAiSuggestionId,
    setLastSelectedAiSuggestionId,
    acceptSuggestion,
    cancelSuggestion,
    isLoadingAcceptSuggestion,
    sendUserMessage,
    isChatLoading = false,
    children,
}: Props) {
    const providerContext = useMemo(
        () => ({
            aiAgent,
            selectedAiSuggestionId,
            setSelectedAiSuggestionId,
            lastSelectedAiSuggestionId,
            setLastSelectedAiSuggestionId,
            acceptSuggestion,
            cancelSuggestion,
            isLoadingAcceptSuggestion,
            sendUserMessage,
            isChatLoading,
        }),
        [
            aiAgent,
            selectedAiSuggestionId,
            lastSelectedAiSuggestionId,
            acceptSuggestion,
            cancelSuggestion,
            isLoadingAcceptSuggestion,
            sendUserMessage,
            isChatLoading,
        ]
    )

    return (
        <ChatContext.Provider value={providerContext}>
            {children}
        </ChatContext.Provider>
    )
}

export const useChat = () => {
    return useContext(ChatContext)
}
